import { forwardRef, useEffect, useState, type ReactNode } from 'react';
import {
  Text,
  Group,
  Tooltip,
  Stack,
  Loader,
  Center,
  Progress,
  ActionIcon,
  Menu,
  Badge,
  Box,
  Card,
} from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { ViewPanel } from '@ayocore/platform/client/components/EntityViewLayout';
import type { LeadEvent } from '../../../../../schemas/lead-event.schema';
import type { LeadStatus } from '../../../../../schemas/lead-status.schema';
import { useLeadStatusRepository } from '../../../../rpc';
import logger from '@ayocore/platform/common/helpers/logger';

/**
 * Колонка funnel-а: интерактивный wrapper с hover-фоном для кликабельных статусов.
 * Расширяет hit-зону (padding) и явно сигнализирует кликабельность.
 *
 * forwardRef нужен для Mantine Tooltip — без него tooltip не получает hover-сигналы.
 */
const StatusCell = forwardRef<
  HTMLDivElement,
  { isClickable: boolean; onClick?: () => void; children: ReactNode }
>(({ isClickable, onClick, children }, ref) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      ref={ref}
      onClick={isClickable ? onClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: isClickable ? 'pointer' : 'default',
        padding: '4px 6px',
        borderRadius: 6,
        background: hovered && isClickable ? 'var(--mantine-color-gray-1)' : 'transparent',
        transition: 'background 0.1s ease',
        // Растягиваем ячейку на всю высоту, чтобы внутренний Stack мог
        // прижать Progress к низу через marginTop: auto.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </Box>
  );
});
StatusCell.displayName = 'StatusCell';

interface LeadStatusHistoryProps {
  statusHistory: LeadEvent[];
  currentStatusId: number | null;
  leadTypeId: number | null;
  onStatusClick?: (status: LeadStatus) => void;
}

/**
 * Компонент для отображения всех возможных статусов лида.
 * Показывает все статусы для типа лида:
 * - Статусы, которые уже были (есть в истории) - отображаются filled
 * - Статусы, которые еще не были - отображаются outline
 */
export function LeadStatusHistory({
  statusHistory,
  currentStatusId,
  leadTypeId,
  onStatusClick,
}: LeadStatusHistoryProps) {
  const [allStatuses, setAllStatuses] = useState<LeadStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const leadStatusRepository = useLeadStatusRepository();

  // Загружаем все статусы для типа лида
  useEffect(() => {
    if (!leadTypeId) {
      setAllStatuses([]);
      setLoading(false);
      return;
    }

    const loadStatuses = async () => {
      try {
        setLoading(true);
        const statuses = await leadStatusRepository.findByLeadType(leadTypeId);
        setAllStatuses(statuses);
      } catch (error) {
        logger.error('Failed to load lead statuses:', error);
        setAllStatuses([]);
      } finally {
        setLoading(false);
      }
    };

    loadStatuses();
  }, [leadTypeId]);

  // Извлекаем ID статусов, которые уже были в истории
  const passedStatusIds = new Set<number>();
  if (statusHistory && statusHistory.length > 0) {
    statusHistory.forEach(event => {
      if (event.to_status?.id) {
        passedStatusIds.add(event.to_status.id);
      }
    });
  }

  // Текущий статус тоже считается пройденным (даже если его нет в истории)
  if (currentStatusId) {
    passedStatusIds.add(currentStatusId);
  }

  // Находим информацию о событии для каждого пройденного статуса (для tooltip)
  const getEventInfo = (statusId: number) => {
    const event = statusHistory?.find(e => e.to_status?.id === statusId);
    return event
      ? {
          date: event.created_at,
          createdBy: event.created_by,
          source: event.source,
        }
      : null;
  };

  // Форматирование даты для tooltip
  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Форматирование источника события
  const formatSource = (source: LeadEvent['source']) => {
    switch (source) {
      case 'user':
        return 'Пользователь';
      case 'agent':
        return 'Агент';
      case 'cron':
        return 'Система';
      default:
        return source;
    }
  };

  // Сортируем статусы по sort_order для корректного отображения прогресса
  const sortedStatuses = [...allStatuses].sort((a, b) => {
    const aOrder = a.sort_order ?? 0;
    const bOrder = b.sort_order ?? 0;
    return aOrder - bOrder;
  });

  // Позитивный флоу — основной ряд статусов
  const positiveStatuses = sortedStatuses.filter(s => !s.is_negative);
  // Негативные статусы — показываем в [...] меню, кроме текущего (если сбылся)
  const negativeStatuses = sortedStatuses.filter(s => s.is_negative);
  const currentIsNegative = negativeStatuses.some(s => s.id === currentStatusId);

  // Статусы для отображения в ряду: позитивные + текущий негативный (если сбылся)
  const visibleStatuses = currentIsNegative
    ? [
        ...positiveStatuses.filter(s => !s.is_final),
        ...negativeStatuses.filter(s => s.id === currentStatusId),
      ]
    : positiveStatuses;

  // Негативные для меню [...]: все негативные кроме текущего (он уже в ряду)
  const menuNegativeStatuses = negativeStatuses.filter(s => s.id !== currentStatusId);

  const currentIndex = currentStatusId
    ? visibleStatuses.findIndex(status => status.id === currentStatusId)
    : -1;

  // Дата входа в статус (если лид когда-либо заходил в него)
  const getEnteredAt = (statusId: number): Date | null => {
    const enterEvent = statusHistory.find(
      e => e.event_type === 'status_change' && e.to_status?.id === statusId
    );
    return enterEvent ? new Date(enterEvent.created_at) : null;
  };

  // Форматируем дату входа: сегодня → ЧЧ:ММ, вчера → «Вчера ЧЧ:ММ», иначе → ДД.ММ.ГГГГ ЧЧ:ММ
  const formatEnteredAt = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const hhmm = `${pad(date.getHours())}:${pad(date.getMinutes())}`;

    const now = new Date();
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (sameDay(date, now)) return hhmm;

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(date, yesterday)) return `Вчера ${hhmm}`;

    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${hhmm}`;
  };

  // Вычисляем количество дней в каждом статусе
  const getDaysInStatus = (statusId: number): number | null => {
    // Фильтруем только события смены статуса
    const statusChangeEvents = statusHistory.filter(e => e.event_type === 'status_change');

    // Находим событие входа в статус (to_status_id = statusId)
    const enterEvent = statusChangeEvents.find(e => e.to_status?.id === statusId);
    if (!enterEvent) {
      return null; // Лид никогда не был в этом статусе
    }

    const enterDate = new Date(enterEvent.created_at);

    // Находим событие выхода из статуса (from_status_id = statusId)
    // Ищем следующее событие после входа, где from_status_id = statusId
    const exitEvent = statusChangeEvents.find(
      e => e.from_status?.id === statusId && new Date(e.created_at) > enterDate
    );

    const exitDate = exitEvent ? new Date(exitEvent.created_at) : new Date(); // Если нет выхода, значит это текущий статус

    // Вычисляем разницу в днях
    const diffMs = exitDate.getTime() - enterDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return Math.floor(diffDays);
  };

  return (
    <Group gap={4} wrap='nowrap' align='stretch'>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ViewPanel>
          {loading ? (
            <Center>
              <Loader size='xs' />
            </Center>
          ) : sortedStatuses.length === 0 ? (
            <Text size='xs' c='dimmed'>
              {leadTypeId ? 'Нет доступных статусов' : 'Тип лида не указан'}
            </Text>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${visibleStatuses.length}, 1fr)`,
                alignItems: 'stretch',
                gap: 0,
              }}
            >
              {visibleStatuses.map((status, idx) => {
                const isPassed = passedStatusIds.has(status.id);
                const isCurrent = status.id === currentStatusId;
                const isClickable = onStatusClick != null && !isCurrent;
                const isNegative = status.is_negative;
                const isBeforeCurrent = currentIndex !== -1 && idx <= currentIndex;
                const isAfterCurrent = currentIndex !== -1 && idx > currentIndex;
                const isActivelyPassed = isPassed && !isAfterCurrent;
                const daysInStatus = getDaysInStatus(status.id);
                const enteredAt = getEnteredAt(status.id);

                let progressColor: string;
                let progressValue: number;
                if (isNegative && isCurrent) {
                  progressColor = 'red';
                  progressValue = 100;
                } else if (isCurrent || isBeforeCurrent) {
                  progressColor = 'green';
                  progressValue = 100;
                } else if (isAfterCurrent && isPassed) {
                  // Побывали в статусе, но потом откатились назад — тот же
                  // серый, что и у непосещённого статуса (gray.5, см. ветку
                  // ниже), но на пару тонов темнее (primaryShade: 5 в теме).
                  progressColor = 'gray.5';
                  progressValue = 100;
                } else {
                  progressColor = 'gray';
                  progressValue = 0;
                }

                const tooltipLabel = isPassed
                  ? (() => {
                      const eventInfo = getEventInfo(status.id);
                      const tooltipParts: string[] = [];
                      if (eventInfo) {
                        tooltipParts.push(`Дата: ${formatDate(eventInfo.date)}`);
                        if (eventInfo.createdBy?.name)
                          tooltipParts.push(`Пользователь: ${eventInfo.createdBy.name}`);
                        tooltipParts.push(`Источник: ${formatSource(eventInfo.source)}`);
                      }
                      if (daysInStatus !== null)
                        tooltipParts.push(`Дней в статусе: ${daysInStatus}`);
                      return tooltipParts.length > 0
                        ? tooltipParts.join('\n')
                        : 'Статус был пройден';
                    })()
                  : isClickable
                    ? 'Изменить статус'
                    : null;

                return (
                  <Tooltip
                    key={status.id}
                    label={tooltipLabel}
                    withArrow
                    disabled={tooltipLabel == null}
                  >
                    <StatusCell
                      isClickable={isClickable}
                      onClick={isClickable ? () => onStatusClick(status) : undefined}
                    >
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Group gap={6} wrap='nowrap' align='center'>
                          <Text
                            size='xs'
                            fw={isActivelyPassed ? 600 : 400}
                            c={
                              isNegative && isCurrent ? 'red' : isActivelyPassed ? 'dark' : 'dimmed'
                            }
                            ta='left'
                            style={{ fontSize: '0.7rem' }}
                          >
                            {status.name}
                          </Text>
                          {daysInStatus !== null && (
                            <Badge
                              size='xs'
                              variant='light'
                              color={isNegative && isCurrent ? 'red' : isCurrent ? 'green' : 'gray'}
                              radius='sm'
                            >
                              {daysInStatus}д
                            </Badge>
                          )}
                        </Group>
                        <Text
                          size='xs'
                          c='dimmed'
                          ta='left'
                          style={{
                            fontSize: '0.65rem',
                            visibility: enteredAt ? 'visible' : 'hidden',
                          }}
                        >
                          {enteredAt
                            ? `${formatEnteredAt(enteredAt)}${(() => {
                                const info = getEventInfo(status.id);
                                return info?.createdBy?.name ? ` · ${info.createdBy.name}` : '';
                              })()}`
                            : ' '}
                        </Text>
                        <Progress
                          size='md'
                          color={progressColor}
                          value={progressValue}
                          transitionDuration={0}
                          style={{ marginTop: 'auto' }}
                        />
                      </Stack>
                    </StatusCell>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </ViewPanel>
      </div>

      {menuNegativeStatuses.length > 0 && (
        <Card
          padding='xs'
          shadow='none'
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Menu withArrow position='bottom-end' withinPortal>
            <Menu.Target>
              <ActionIcon variant='subtle' size='xs' color='red'>
                <IconDots size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {menuNegativeStatuses.map(status => (
                <Menu.Item
                  key={status.id}
                  color='red'
                  disabled={!onStatusClick}
                  onClick={() => onStatusClick?.(status)}
                >
                  {status.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Card>
      )}
    </Group>
  );
}
